/**
 * Keeping your place in the results list when the filters change.
 *
 * Tweaking a filter used to send the list back to the top. That is cheap to
 * implement and expensive to use: the whole point of a filter is to adjust a
 * search you are already reading, and being thrown back to September 10th every
 * time you do costs more than the filter saves.
 *
 * So the list is re-anchored instead. Whatever card sat at the middle of the
 * pane is put back where it was. If the new filters exclude it, its *moment in
 * the week* still anchors the list — the user was reading Monday at 9am, so the
 * new results open at Monday at 9am rather than at the beginning of the week.
 *
 * The API does the finding: the card's id is sent as `anchor`, and the first
 * page starts at that event's position in the order whether or not it still
 * matches. What is left here is reading which card that was, and where it sat.
 */

export interface ListAnchor {
  id: string;
  /** Where the card's top edge sat relative to the pane's top, in pixels. */
  offsetTop: number;
}

/**
 * How far from the pane's midpoint to look for a card, in pixels.
 *
 * The exact midpoint often lands in the gutter between two cards, or on the
 * sticky date header — neither of which is an anchor. Probing outward finds the
 * nearest real card instead of giving up.
 */
const PROBES = [0, -28, 28, -56, 56, -92, 92, -140, 140, -200, 200];

/** The card nearest the middle of the pane, or null if none is on screen. */
export function readAnchor(container: HTMLElement): ListAnchor | null {
  const pane = container.getBoundingClientRect();
  const x = pane.left + pane.width / 2;
  const middle = pane.top + pane.height / 2;

  for (const dy of PROBES) {
    const y = middle + dy;
    if (y < pane.top || y > pane.bottom) continue;

    const hit = document.elementFromPoint(x, y);
    const card = hit?.closest<HTMLElement>('[data-event-id]');
    if (!card) continue;

    const id = card.dataset.eventId;
    if (!id) continue;

    return { id, offsetTop: card.getBoundingClientRect().top - pane.top };
  }

  return null;
}
